import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAddInteractionMutation } from 'src/api/interactions';
import { InteractionStatusEnum } from 'src/api/interactions/types';
import {
  useGetInteractionDictionaryQuery,
  useGetInteractionsDictionaryQuery,
} from 'src/api/interactions-dictionary';
import { useGlobalModals } from 'src/contexts/global-modals';
import { useFormContext } from 'src/form';
import { validateEmpty } from 'src/helpers/validators';
import { links } from 'src/router/routes';

export type FormData = {
  name: string;
  with_leaderboard: boolean;
  with_pvo: boolean;
  with_pvo_form: boolean;
  language: string;
  status: InteractionStatusEnum;
  interaction_dictionary_id: string;
  selected_theme_id: string;
};

const validate = {
  interaction_dictionary_id: (value: string) =>
    validateEmpty(value, 'Interaction dictionary is required'),
  language: (value: string) => validateEmpty(value, 'Language is required'),
  name: (value: string) => validateEmpty(value, 'Name is required'),
  selected_theme_id: (value: string) =>
    validateEmpty(value, 'Theme is required'),
};

export const useAddInteraction = () => {
  const { dictionaryId } = useParams<{ dictionaryId?: string }>();
  const navigate = useNavigate();

  const { openLoadingModal, closeLoadingModal } = useGlobalModals();

  const { addInteractionMutation, isPending: isAddPending } =
    useAddInteractionMutation();

  const { data: interactionDictionaries, isPending } =
    useGetInteractionsDictionaryQuery();

  const initialValues: FormData = {
    interaction_dictionary_id: dictionaryId || '',
    language: 'en',
    name: '',
    selected_theme_id: '',
    status: InteractionStatusEnum.ACTIVE,
    with_leaderboard: false,
    with_pvo: false,
    with_pvo_form: false,
  };

  const interactionDictionaryOptions =
    interactionDictionaries?.map((dict) => ({
      label: dict.name,
      value: dict.id.toString(),
    })) || [];

  const handleSubmit = async (values: FormData) => {
    openLoadingModal(
      'Adding interaction',
      'Please wait while we add the interaction...'
    );

    try {
      await addInteractionMutation({
        interaction_dictionary_id: parseInt(values.interaction_dictionary_id),
        language: values.language,
        name: values.name,
        selected_theme_id: parseInt(values.selected_theme_id),
        status: values.status,
        with_leaderboard: values.with_leaderboard,
        with_pvo: values.with_pvo,
        with_pvo_form: values.with_pvo_form,
      });

      navigate(links.interactions);
    } catch (error) {
      console.error('Failed to add interaction:', error);
    } finally {
      closeLoadingModal();
    }
  };

  const handleCancel = () => {
    navigate(links.interactions);
  };

  return {
    handleSubmit,
    handleCancel,
    initialValues,
    interactionDictionaryOptions,
    isAddPending,
    isPending,
    validate,
  };
};

export const useInteractionThemes = () => {
  const form = useFormContext();
  const selectedDictionaryId = (form.values as FormData)
    .interaction_dictionary_id;

  const { data: interactionDictionary } = useGetInteractionDictionaryQuery(
    selectedDictionaryId ? parseInt(selectedDictionaryId) : 0
  );

  const themeOptions =
    interactionDictionary?.themes?.map((theme) => ({
      image: theme.image,
      label: theme.name,
      value: theme.id.toString(),
    })) || [];

  return { themeOptions };
};
